'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useMergeRefs } from '@floating-ui/react'
import { forwardRef, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useResizeObserver } from 'usehooks-ts'

type ScrollableYProps = {
  children: ReactNode
  className?: string
  onScrollDownChanged?: (isScrolledDown: boolean) => void
}

const DATA_ATTRIBUTE = 'data-scroll'

/** Adds top/bottom border around children based on scroll state */
export const ScrollableY = forwardRef<HTMLDivElement, ScrollableYProps>(function ScrollableY(
  { children, className, onScrollDownChanged },
  forwardedRef,
) {
  const ref = useRef<HTMLDivElement>(null)
  const isScrolledDownRef = useRef<boolean | null>(null)

  const mergedRef = useMergeRefs([ref, forwardedRef])

  // -- Update scroll state
  const updateScroll = useCallback(() => {
    const { current } = ref

    if (!current) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = current

    const nothingToScroll = scrollHeight + 1 <= clientHeight + 2

    if (nothingToScroll) {
      current.setAttribute(DATA_ATTRIBUTE, 'none')
    } else if (scrollTop === 0) {
      current.setAttribute(DATA_ATTRIBUTE, 'top')
    } else if (scrollTop === scrollHeight - clientHeight) {
      current.setAttribute(DATA_ATTRIBUTE, 'bottom')
    } else {
      current.setAttribute(DATA_ATTRIBUTE, 'both')
    }

    const isScrolledDown = !nothingToScroll && scrollTop > 0

    if (isScrolledDownRef.current !== isScrolledDown) {
      isScrolledDownRef.current = isScrolledDown
      onScrollDownChanged?.(isScrolledDown)
    }
  }, [onScrollDownChanged])

  // -- Update scroll state on resize

  //@ts-expect-error https://github.com/juliencrn/usehooks-ts/issues/602
  const { height = Infinity } = useResizeObserver({ ref })

  // Update on height change
  useEffect(() => {
    updateScroll()
  }, [height, updateScroll])

  useEffect(() => {
    const { current } = ref
    if (!current) return

    // Scroll listener
    const handleScroll = () => updateScroll()
    current.addEventListener('scroll', handleScroll)

    // Mutation observer (for dynamic content changes)
    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateScroll)
    })

    observer.observe(current, {
      childList: true,
      subtree: true,
      attributes: false, // leave to false to avoid infinite loop
    })

    // Initial update
    updateScroll()

    // Cleanup
    return () => {
      current.removeEventListener('scroll', handleScroll)
      observer.disconnect()
    }
  }, [updateScroll])

  return (
    <div
      ref={mergedRef}
      tabIndex={0}
      className={twMerge(
        className,
        '-mx-1 min-h-0 overflow-y-auto border-y-px border-brand-2-100 p-1',
        'data-[scroll=top]:!border-t-clear',
        'data-[scroll=bottom]:!border-b-clear',
        'data-[scroll=none]:!overflow-y-visible data-[scroll=none]:!border-y-clear',
      )}
    >
      {children}
    </div>
  )
})
